import React, { useState } from "react";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";

import PaywallCTAModal from "components/account_management/PaywallCTAModal";
import { AiSource } from "components/ai_chat/types";
import { LoadingContent } from "components/bid_module/common_report_components/LoadingContent";
import ReportCollapse from "components/bid_module/common_report_components/ReportCollapse";
import FindEntity from "components/entities/FindEntity";
import Button from "components/ui/button/Button";
import Card from "components/ui/card/Card";
import EmptyState from "components/ui/empty_state/EmptyState";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import { AnswerMarkdown } from "lib/core_components/ai/AnswerMarkdown";
import { AiChatStreamResponse_Sources } from "lib/generated/app-service-gql/graphql";
import { useGenerateBuyerActivity } from "lib/hooks/api/buyer_activity/useGenerateBuyerActivity";
import { useRefreshBuyerActivity } from "lib/hooks/api/buyer_activity/useRefreshBuyerActivity";
import { usePqlActioned } from "lib/hooks/api/users/usePqlActioned";
import { useSubscription } from "lib/hooks/subscription";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { AiContextProvider } from "lib/providers/AiContextProvider";
import { useDialogManager } from "lib/providers/DialogManager";
import {
  BuyerActivityObjectType,
  BuyerActivityStatus,
  PqlActionType,
} from "lib/types/graphQLEnums";
import { pluralise } from "lib/utils";
import { Flex, Text } from "../../styles/utility-components";
import BuyerActivityHeader from "./BuyerActivityHeader";
import RecommendedNotices from "./RecommendedNotices";
import { CoreNotice } from "./types";
import { useBuyerActivityCompletion } from "./useBuyerActivityCompletion";

const QUESTIONS_TO_EXCLUDE_FOR_REQUIREMENT_SUMMARY_REPORT = [
  "Buyer's commitment to this type of product/service:",
  "Likelihood of renewal:",
];

type BuyerActivityResult = {
  status?: BuyerActivityStatus | null;
  questions: {
    question: string;
    answer: string;
    sources?: AiChatStreamResponse_Sources[] | null;
  }[];
  relatedNotices?: CoreNotice[] | null;
};

type BuyerActivityProps = {
  summary: BuyerActivityResult | null;
  notices: CoreNotice[];
  objectId: string;
  objectType: BuyerActivityObjectType;
  noticesSearchUrl: string;
  saveNoticeButton?: JSX.Element;
  /**
   * Whether to render {@link BuyerActivityHeader} (report title, back-links, Regenerate). True for
   * a standalone report page. The pre-POC lead overview embeds this report in a page that already
   * has its own title and a link to itself, so it opts out and keeps Regenerate in the Overview
   * collapse instead — remove that path along with the unified contacts gate.
   */
  showHeader?: boolean;
  bidName?: string | null;
  leadName?: string | null;
  buyerName?: string | null;
  buyerId?: string | null;
  noticeId?: string | null;
  noticeTitle?: string | null;
};

export function BuyerActivity({
  summary,
  notices,
  objectId,
  objectType,
  noticesSearchUrl,
  saveNoticeButton,
  showHeader = true,
  bidName,
  leadName,
  buyerName,
  buyerId,
  noticeId,
  noticeTitle,
}: BuyerActivityProps) {
  const { colors } = useTheme();
  const dialogManager = useDialogManager();
  const { mutate: actionPql } = usePqlActioned();

  const subscription = useSubscription();
  const isFreeOrBasic =
    !subscription.hasDataTypes("BUYERS|SUPPLIERS") && !subscription.hasDataTypes("BIDS");

  const { mutate: refreshReport } = useRefreshBuyerActivity();

  const isStatusPending = summary?.status === BuyerActivityStatus.Pending;
  const isStatusError = summary?.status === BuyerActivityStatus.Error;

  useBuyerActivityCompletion({ status: summary?.status, notices, objectId, objectType });

  const handleRequestDemo = () => {
    actionPql({
      actionType: PqlActionType.BookADemoInApp,
      dataType: objectType.toUpperCase(),
      pageUrl: window.location.href,
    });

    void dialogManager.openDialog(PaywallCTAModal, {
      showBookADemoForm: true,
      featureType: "AWARDS",
    });
  };

  const handleComparePlans = () => {
    actionPql({
      actionType: PqlActionType.CompareAllPlans,
      dataType: objectType.toUpperCase(),
      pageUrl: window.location.href,
    });

    void dialogManager.openDialog(PaywallCTAModal, {
      showBookADemoForm: false,
      featureType: "AWARDS",
    });
  };

  return (
    <AiContextProvider
      context={{
        resourceId: objectId,
        resourceName: "Buyer activity",
        resourceType: "BUYER_ACTIVITY",
        bidId: objectType === BuyerActivityObjectType.Bid ? objectId : undefined,
      }}
    >
      <Flex column gap={24} aria-label="buyer-activity">
        {showHeader && (
          <BuyerActivityHeader
            objectId={objectId}
            objectType={objectType}
            summary={summary}
            notices={notices}
            bidName={bidName}
            leadName={leadName}
            buyerName={buyerName}
            buyerId={buyerId}
            noticeId={noticeId}
            noticeTitle={noticeTitle}
          />
        )}
        {isFreeOrBasic && (
          <Card backgroundColor={colors.sysTextDefault} data-testid="buyer-activity-paywall-banner">
            <Flex justifyContent="space-between" alignItems="center">
              <Flex gap={8} alignItems="center">
                <UIcon icon="lock01" size={20} color="white" />
                <Text description white>
                  This feature is now free for everyone - limited time only!
                </Text>
              </Flex>
              <Flex gap={8} alignItems="center">
                <Button buttonStyle="inverse" onClick={handleRequestDemo}>
                  Request a demo
                </Button>
                <Button buttonType="secondary" buttonStyle="inverse" onClick={handleComparePlans}>
                  Compare all plans
                </Button>
              </Flex>
            </Flex>
          </Card>
        )}
        <BuyerActivitySummaryContainer
          summary={summary}
          notices={notices}
          objectId={objectId}
          objectType={objectType}
          isLoading={isStatusPending}
          isError={isStatusError}
          isEmptyNoticeState={notices.length === 0 && !summary}
          showRegenerateInOverview={!showHeader}
          noticesSearchUrl={noticesSearchUrl}
          saveNoticeButton={saveNoticeButton}
          onRegenerate={() =>
            refreshReport({ GenerateBuyerActivitySummaryRequest: { objectId, objectType } })
          }
          canRegenerate={!isStatusPending && notices.length > 0}
          isRegenerating={isStatusPending}
        />
      </Flex>
    </AiContextProvider>
  );
}

function BuyerActivitySummaryContainer({
  summary,
  notices,
  objectId,
  objectType,
  noticesSearchUrl,
  isEmptyNoticeState,
  isLoading,
  isError,
  saveNoticeButton,
  showRegenerateInOverview,
  onRegenerate,
  canRegenerate,
  isRegenerating,
}: {
  summary: BuyerActivityResult | null;
  notices: CoreNotice[];
  objectId: string;
  objectType: BuyerActivityObjectType;
  noticesSearchUrl: string;
  isEmptyNoticeState: boolean;
  isLoading: boolean;
  isError: boolean;
  saveNoticeButton?: JSX.Element;
  showRegenerateInOverview: boolean;
  onRegenerate: () => void;
  canRegenerate: boolean;
  isRegenerating: boolean;
}) {
  const { mutate: generateReport } = useGenerateBuyerActivity();

  if (isError) {
    return (
      <EmptyState
        type="loadError"
        heading="Sorry, something went wrong while processing your request."
        description="Please try regenerating the buyer activity. If the error persists, contact support."
        width="100%"
        size="large"
        action={
          // The standalone report page already has a Regenerate button in its header, so only
          // offer one here in the embedded lead overview, where the header is hidden.
          showRegenerateInOverview ? (
            <RegenerateButton
              onRegenerate={onRegenerate}
              canRegenerate={canRegenerate}
              isRegenerating={isRegenerating}
            />
          ) : undefined
        }
      />
    );
  }

  return (
    <BuyerActivitySummary
      summary={summary}
      notices={notices}
      isLoading={isLoading}
      objectType={objectType}
      saveNoticeButton={saveNoticeButton}
      isEmptyNoticeState={isEmptyNoticeState}
      noticesSearchUrl={noticesSearchUrl}
      showRegenerateInOverview={showRegenerateInOverview}
      onGenerateReport={() =>
        generateReport({ GenerateBuyerActivitySummaryRequest: { objectId, objectType } })
      }
      onRegenerate={onRegenerate}
      canRegenerate={canRegenerate}
      isRegenerating={isRegenerating}
    />
  );
}

function OverviewContent({
  isEmptyNoticeState,
  noticesSearchUrl,
  summary,
  isLoading,
  objectType,
  onGenerateReport,
}: {
  isEmptyNoticeState: boolean;
  noticesSearchUrl?: string;
  summary: BuyerActivityResult | null;
  isLoading: boolean;
  objectType: BuyerActivityObjectType;
  onGenerateReport?: () => void;
}) {
  if (isEmptyNoticeState) {
    return <FindEntity entity="notice" url={noticesSearchUrl} isEmptyState />;
  }
  if (!summary) {
    return (
      <Flex justifyContent="center" alignItems="center">
        <EmptyState
          type="userRequiredContent"
          heading="Understand historical buyer activity and relationships"
          description="Learn more about this buyer to inform your qualification and approach to this deal."
          size="small"
          width="400"
          action={
            <Button onClick={() => onGenerateReport?.()} aria-label="Generate report">
              Generate report
            </Button>
          }
        />
      </Flex>
    );
  }
  return (
    <QuestionsContainer column>
      {summary.questions
        .filter(({ question }) => {
          if (objectType === BuyerActivityObjectType.Lead) return true;
          return !QUESTIONS_TO_EXCLUDE_FOR_REQUIREMENT_SUMMARY_REPORT.includes(question);
        })
        .map((question) => (
          <Flex key={question.question} column>
            <Text h3>{question.question}</Text>
            {isLoading ? (
              <Flex column gap={8}>
                <SkeletonText width="100%" fontSize={21} variant="rectangular" />
                <SkeletonText width="100%" fontSize={21} variant="rectangular" />
                <SkeletonText width="100%" fontSize={21} variant="rectangular" />
                <SkeletonText width="100%" fontSize={21} variant="rectangular" />
              </Flex>
            ) : (
              <AnswerMarkdown
                answer={question.answer}
                sources={(question.sources ?? []) as AiSource[]}
              />
            )}
          </Flex>
        ))}
    </QuestionsContainer>
  );
}

function RegenerateButton({
  onRegenerate,
  canRegenerate,
  isRegenerating,
}: {
  onRegenerate?: () => void;
  canRegenerate: boolean;
  isRegenerating: boolean;
}) {
  return (
    <Button
      onClick={() => onRegenerate?.()}
      disabled={!canRegenerate}
      buttonType="secondary"
      icon="refreshCcw01"
      aria-label="Refresh report"
    >
      {isRegenerating ? "Regenerating..." : "Regenerate"}
    </Button>
  );
}

function overviewActions({
  isLoading,
  showRegenerate,
  isRegenerating,
  canRegenerate,
  onRegenerate,
}: {
  isLoading: boolean;
  showRegenerate: boolean;
  isRegenerating: boolean;
  canRegenerate: boolean;
  onRegenerate?: () => void;
}): React.ReactNode {
  if (isLoading) return <LoadingContent />;
  if (!showRegenerate) return null;
  return (
    <RegenerateButton
      onRegenerate={onRegenerate}
      canRegenerate={canRegenerate}
      isRegenerating={isRegenerating}
    />
  );
}

function BuyerActivitySummary({
  summary,
  notices,
  isLoading,
  objectType,
  saveNoticeButton,
  isEmptyNoticeState = false,
  noticesSearchUrl,
  showRegenerateInOverview = false,
  onGenerateReport,
  onRegenerate,
  canRegenerate = false,
  isRegenerating = false,
}: {
  summary: BuyerActivityResult | null;
  notices: CoreNotice[];
  isLoading: boolean;
  objectType: BuyerActivityObjectType;
  saveNoticeButton?: JSX.Element;
  isEmptyNoticeState?: boolean;
  noticesSearchUrl?: string;
  showRegenerateInOverview?: boolean;
  onGenerateReport?: () => void;
  onRegenerate?: () => void;
  canRegenerate?: boolean;
  isRegenerating?: boolean;
}) {
  const [isSimilarNoticesOpen, setIsSimilarNoticesOpen] = useState(true);

  const relatedNotices = summary?.relatedNotices ?? [];
  const allNotices = [...relatedNotices, ...notices];
  const noticesIdentifiedCount = allNotices.length;
  const showRelevantNotices = isLoading || noticesIdentifiedCount > 0;
  const showRegenerate = showRegenerateInOverview && summary !== null;

  return (
    <>
      <ReportCollapse
        title="Overview"
        actions={overviewActions({
          isLoading,
          showRegenerate,
          isRegenerating,
          canRegenerate,
          onRegenerate,
        })}
      >
        <OverviewContent
          isEmptyNoticeState={isEmptyNoticeState}
          noticesSearchUrl={noticesSearchUrl}
          summary={summary}
          isLoading={isLoading}
          objectType={objectType}
          onGenerateReport={onGenerateReport}
        />
      </ReportCollapse>
      {showRelevantNotices && (
        <ReportCollapse
          open={isSimilarNoticesOpen}
          onOpenChange={setIsSimilarNoticesOpen}
          title={
            isLoading
              ? "Relevant procurement notices"
              : pluralise(noticesIdentifiedCount, "relevant procurement notice")
          }
          actions={!isSimilarNoticesOpen && isLoading ? <LoadingContent /> : null}
        >
          <RecommendedNotices
            relatedNotices={relatedNotices}
            savedNotices={notices}
            isLoading={isLoading}
            objectType={objectType}
            saveToButton={saveNoticeButton}
          />
        </ReportCollapse>
      )}
    </>
  );
}

const QuestionsContainer = styled(Flex)({
  marginTop: 24,
  gap: 16,
});
